import type { ReactElement } from 'react';
import { api, type LocationView, type ZoneView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { useAsync } from '../shared/lib/use-async';
import { DataTable, type Column } from '../shared/ui/DataTable';
import { DriversTable } from './DriversTable';

/**
 * Справочники (разд. 12).
 *
 * Диспетчеру доступны только на чтение — матрица прав ТЗ разд. 2.1. Кнопок правки он
 * не видит вовсе: элемент, недоступный роли, не рендерится, а не блокируется —
 * спрятанная кнопка честнее неактивной.
 *
 * `external_code` у локаций стоит первой колонкой не для порядка: по нему CT адресует
 * точку отправления (FR-628), и именно его диспетчер сверяет при разборе.
 */
export function DirectoriesPage({
  section,
  canEdit,
}: {
  readonly section: 'locations' | 'zones' | 'drivers';
  readonly canEdit: boolean;
}): ReactElement {
  const messages = useMessages();

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{messages.cabinet.nav[section]}</h1>
        {canEdit ? null : (
          <span style={{ color: 'var(--text-muted)' }}>
            {messages.cabinet.directories.readOnly}
          </span>
        )}
      </header>

      {section === 'locations' ? <LocationsTable /> : null}
      {section === 'zones' ? <ZonesTable /> : null}
      {section === 'drivers' ? <DriversSection /> : null}
    </div>
  );
}

function LocationsTable(): ReactElement {
  const messages = useMessages();
  const locations = useAsync(() => api.locations(), []);

  const columns: readonly Column<LocationView>[] = [
    {
      key: 'code',
      title: messages.cabinet.directories.externalCode,
      width: '140px',
      render: (row) => <span className="num">{row.external_code}</span>,
    },
    { key: 'name', title: messages.cabinet.directories.name, render: (row) => row.name },
    {
      key: 'type',
      title: messages.cabinet.directories.type,
      width: '120px',
      render: (row) =>
        (messages.cabinet.directories.locationTypes as Record<string, string>)[row.type] ??
        row.type,
    },
    {
      key: 'address',
      title: messages.cabinet.directories.address,
      render: (row) => row.address?.raw ?? '',
    },
  ];

  return (
    <DataTable
      rows={locations.data?.data ?? []}
      columns={columns}
      rowKey={(row) => row.id}
      emptyText={messages.cabinet.common.empty}
    />
  );
}

function ZonesTable(): ReactElement {
  const messages = useMessages();
  const zones = useAsync(() => api.zones(), []);

  const columns: readonly Column<ZoneView>[] = [
    { key: 'name', title: messages.cabinet.directories.name, render: (row) => row.name },
    {
      key: 'mode',
      title: messages.cabinet.order.mode,
      width: '160px',
      render: (row) =>
        row.mode === 'express' || row.mode === 'distribution'
          ? messages.cabinet.modes[row.mode]
          : '',
    },
  ];

  return (
    <DataTable
      rows={zones.data ?? []}
      columns={columns}
      rowKey={(row) => row.id}
      emptyText={messages.cabinet.common.empty}
    />
  );
}

function DriversSection(): ReactElement {
  const drivers = useAsync(() => api.drivers(), []);

  return <DriversTable rows={drivers.data?.data ?? []} loading={drivers.loading} />;
}
